import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Building2, Search, Sparkles, DollarSign, ReceiptText, Send } from "lucide-react";
import { TIPO_ENTRADA_AGENCIA, TIPO_ENTRADA_BADGE } from "@/lib/constants";
import TarifasAgenciaDialog from "@/pages/config/TarifasAgenciaDialog";

const VACIO = {
  nombre: "", tipo_entrada: "correo_manual", remitentes: "", facturada: true, activo: true,
  cierre_manual_alertar: false, contacto_cierre: "", correos_aviso: "",
};

function AgenciaDialog({ open, onOpenChange, agencia, onSaved }) {
  const isEdit = !!agencia;
  const [form, setForm] = useState(VACIO);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open) {
      setForm(
        agencia
          ? {
              nombre: agencia.nombre || "",
              tipo_entrada: agencia.tipo_entrada || "correo_manual",
              remitentes: (agencia.remitentes || []).join(", "),
              facturada: agencia.facturada !== false,
              activo: agencia.activo !== false,
              cierre_manual_alertar: !!agencia.cierre_manual_alertar,
              contacto_cierre: agencia.contacto_cierre || "",
              correos_aviso: (agencia.correos_aviso || []).join(", "),
            }
          : VACIO
      );
    }
  }, [open, agencia]);

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indicá el nombre de la agencia.");
    setSaving(true);
    const payload = {
      nombre: form.nombre.trim(),
      tipo_entrada: form.tipo_entrada,
      remitentes: form.remitentes
        .split(/[,\n;]/)
        .map((r) => r.trim())
        .filter(Boolean),
      facturada: form.facturada,
      activo: form.activo,
      cierre_manual_alertar: form.cierre_manual_alertar,
      contacto_cierre: form.contacto_cierre.trim() || null,
      // PROMPT 134: a quienes se les manda el aviso de cierre desde la salida (Agenda).
      correos_aviso: form.correos_aviso
        .split(/[\s,;]+/)
        .map((r) => r.trim())
        .filter(Boolean),
    };
    try {
      if (isEdit) {
        await api.put(`/agencias/${agencia.id}`, payload);
        toast.success("Agencia actualizada.");
      } else {
        await api.post("/agencias", payload);
        toast.success("Agencia creada.");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="agencia-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Building2 className="h-5 w-5" /> {isEdit ? "Editar agencia" : "Nueva agencia / canal"}
          </DialogTitle>
          <DialogDescription>
            Detección automática por remitente/dominio. Las reservas de agencia se facturan (sin saldo al cliente).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Nombre</Label>
            <Input
              value={form.nombre}
              onChange={(e) => set({ nombre: e.target.value })}
              placeholder="Ej. Klook, Tours VIP, Civitatis…"
              data-testid="agencia-nombre-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo de entrada</Label>
            <Select value={form.tipo_entrada} onValueChange={(v) => set({ tipo_entrada: v })}>
              <SelectTrigger data-testid="agencia-tipo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TIPO_ENTRADA_AGENCIA).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Remitentes / dominios (separados por coma)</Label>
            <Textarea
              value={form.remitentes}
              onChange={(e) => set({ remitentes: e.target.value })}
              rows={2}
              placeholder="klook.com, reservas@klook.com"
              data-testid="agencia-remitentes-input"
            />
            <p className="text-[11px] text-slate-400">Se usan para reconocer automáticamente los correos de esta agencia.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Correos para avisar los cierres (separados por coma)</Label>
            <Textarea
              value={form.correos_aviso}
              onChange={(e) => set({ correos_aviso: e.target.value })}
              rows={2}
              placeholder="reservas@agencia.com, operaciones@agencia.com"
              data-testid="agencia-correos-aviso-input"
            />
            <p className="text-[11px] text-slate-400">
              A estos correos les llega el aviso de cierre que se manda desde la salida, en la Agenda.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Facturada</p>
              <p className="text-[11px] text-slate-400">No muestra saldo del cliente (va en facturación de la agencia).</p>
            </div>
            <Switch checked={form.facturada} onCheckedChange={(v) => set({ facturada: v })} data-testid="agencia-facturada-switch" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Activa</p>
              <p className="text-[11px] text-slate-400">Las inactivas no se detectan automáticamente.</p>
            </div>
            <Switch checked={form.activo} onCheckedChange={(v) => set({ activo: v })} data-testid="agencia-activo-switch" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Sistema propio · aviso manual de cierre</p>
              <p className="text-[11px] text-slate-500">
                Para agencias como Turitop o Buendía que venden con su propio motor (no se pueden
                cerrar por API). Al llegar una salida al 100% se genera una alerta interna para avisarles.
              </p>
            </div>
            <Switch
              checked={form.cierre_manual_alertar}
              onCheckedChange={(v) => set({ cierre_manual_alertar: v })}
              data-testid="agencia-cierre-manual-switch"
            />
          </div>
          {form.cierre_manual_alertar && (
            <div className="space-y-1.5">
              <Label className="text-xs">Contacto para avisar el cierre (opcional)</Label>
              <Input
                value={form.contacto_cierre}
                onChange={(e) => set({ contacto_cierre: e.target.value })}
                placeholder="Ej. WhatsApp +1 555 1234 o reservas@turitop.com"
                data-testid="agencia-contacto-cierre-input"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="agencia-cancel">Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="agencia-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AgenciasConfig() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [tarifasOpen, setTarifasOpen] = useState(false);
  const [tarifasAg, setTarifasAg] = useState(null);
  const [facturarOpen, setFacturarOpen] = useState(false);
  const [facturarAg, setFacturarAg] = useState(null);
  const [facturarPreview, setFacturarPreview] = useState(null);
  const [facturarLoading, setFacturarLoading] = useState(false);
  const [facturarAplicando, setFacturarAplicando] = useState(false);

  const abrirTarifas = (a) => { setTarifasAg(a); setTarifasOpen(true); };

  const abrirReaplicarFacturada = async (a) => {
    setFacturarAg(a);
    setFacturarOpen(true);
    setFacturarPreview(null);
    setFacturarLoading(true);
    try {
      const { data } = await api.post(`/agencias/${a.id}/reaplicar-facturada`, null, { params: { aplicar: false } });
      setFacturarPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setFacturarOpen(false);
    } finally {
      setFacturarLoading(false);
    }
  };

  const confirmarReaplicarFacturada = async () => {
    if (!facturarAg) return;
    setFacturarAplicando(true);
    try {
      const { data } = await api.post(`/agencias/${facturarAg.id}/reaplicar-facturada`, null, { params: { aplicar: true } });
      toast.success(`Facturada aplicada a ${data.cambiadas} reserva(s). ${data.ya_correctas} ya estaban bien.`);
      setFacturarOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setFacturarAplicando(false);
    }
  };

  const reetiquetar = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/agencias/backfill");
      toast.success(`Re-etiquetadas ${data.total} reserva(s) sin agencia.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBackfilling(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agencias");
      setAll(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (a, campo, valor) => {
    setAll((prev) => prev.map((x) => (x.id === a.id ? { ...x, [campo]: valor } : x)));
    try {
      await api.put(`/agencias/${a.id}`, { [campo]: valor });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setAll((prev) => prev.map((x) => (x.id === a.id ? { ...x, [campo]: !valor } : x)));
    }
  };

  const ql = q.trim().toLowerCase();
  const agencias = all.filter(
    (a) => (tipo === "todos" || a.tipo_entrada === tipo) && (!ql || a.nombre.toLowerCase().includes(ql))
  );

  const abrirNueva = () => { setEditing(null); setDialogOpen(true); };
  const abrirEdit = (a) => { setEditing(a); setDialogOpen(true); };

  const conteo = {
    bokun: all.filter((a) => a.tipo_entrada === "bokun").length,
    correo_formato: all.filter((a) => a.tipo_entrada === "correo_formato").length,
    correo_manual: all.filter((a) => a.tipo_entrada === "correo_manual").length,
  };

  return (
    <div className="space-y-4" data-testid="agencias-config">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Agencias / Canales
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Catálogo extensible de agencias. Toda reserva queda etiquetada con su agencia de origen.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reetiquetar} disabled={backfilling} data-testid="reetiquetar-agencias-btn" className="gap-2">
            {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Re-etiquetar reservas
          </Button>
          <Button onClick={abrirNueva} data-testid="nueva-agencia-btn" className="gap-2">
            <Plus className="h-4 w-4" /> Nueva agencia
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar agencia..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="agencias-search"
          />
        </div>
        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className="w-full sm:w-64" data-testid="agencias-tipo-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos ({all.length})</SelectItem>
            <SelectItem value="correo_manual">Correo manual ({conteo.correo_manual})</SelectItem>
            <SelectItem value="correo_formato">Correo con formato ({conteo.correo_formato})</SelectItem>
            <SelectItem value="bokun">Bókun ({conteo.bokun})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Agencia", "Tipo de entrada", "Remitentes / dominios", "Reservas", "Facturada", "Activa", ""].map((h, i) => (
                  <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" />
                  </TableCell>
                </TableRow>
              ) : agencias.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-slate-400 text-sm">
                    No hay agencias que coincidan.
                  </TableCell>
                </TableRow>
              ) : (
                agencias.map((a) => (
                  <TableRow key={a.id} data-testid={`agencia-row-${a.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-slate-900">
                      {a.nombre}
                      {a.auto_creada && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-600">
                          <Sparkles className="h-3 w-3" /> auto
                        </span>
                      )}
                      {a.cierre_manual_alertar && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                          data-testid={`agencia-badge-cierre-manual-${a.id}`}
                          title="Sistema propio: se avisa cuando la salida llega al 100%"
                        >
                          Aviso manual
                        </span>
                      )}
                      {a.correos_aviso?.length > 0 && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[10px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                          data-testid={`agencia-badge-correos-aviso-${a.id}`}
                          title={`Aviso de cierre a: ${a.correos_aviso.join(", ")}`}
                        >
                          <Send className="h-3 w-3" /> {a.correos_aviso.length}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${TIPO_ENTRADA_BADGE[a.tipo_entrada]}`}>
                        {TIPO_ENTRADA_AGENCIA[a.tipo_entrada] || a.tipo_entrada}
                      </span>
                    </TableCell>
                    <TableCell className="text-[12px] text-slate-500 max-w-[240px] truncate">
                      {(a.remitentes || []).join(", ") || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{a.reservas_count ?? 0}</TableCell>
                    <TableCell>
                      <Switch
                        checked={a.facturada !== false}
                        onCheckedChange={(v) => toggle(a, "facturada", v)}
                        data-testid={`agencia-facturada-${a.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={a.activo !== false}
                        onCheckedChange={(v) => toggle(a, "activo", v)}
                        data-testid={`agencia-activo-${a.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => abrirReaplicarFacturada(a)}
                        data-testid={`reaplicar-facturada-${a.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-amber-600"
                        title="Aplicar Facturada a sus reservas"
                      >
                        <ReceiptText className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => abrirTarifas(a)}
                        data-testid={`tarifas-agencia-${a.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-emerald-600"
                        title="Tarifas y configuración del portal"
                      >
                        <DollarSign className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => abrirEdit(a)}
                        data-testid={`edit-agencia-${a.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-slate-900"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <AgenciaDialog open={dialogOpen} onOpenChange={setDialogOpen} agencia={editing} onSaved={load} />
      <TarifasAgenciaDialog open={tarifasOpen} onOpenChange={setTarifasOpen} agencia={tarifasAg} onSaved={load} />

      <Dialog open={facturarOpen} onOpenChange={setFacturarOpen}>
        <DialogContent className="max-w-md" data-testid="reaplicar-facturada-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Aplicar Facturada a sus reservas</DialogTitle>
            <DialogDescription>
              {facturarAg && (
                <>
                  Agencia: <b>{facturarAg.nombre}</b> · Facturada actualmente: <b>{facturarAg.facturada !== false ? "Sí" : "No"}</b>.
                  Esto escribe ese mismo valor en todas sus reservas ya cargadas (no toca importes).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {facturarLoading ? (
            <div className="py-6 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : facturarPreview ? (
            <div className="text-sm text-slate-600 space-y-1" data-testid="reaplicar-facturada-preview">
              <p>Total de reservas de esta agencia: <b>{facturarPreview.total}</b></p>
              <p>Van a cambiar: <b className="text-amber-600">{facturarPreview.a_cambiar}</b></p>
              <p>Ya estaban correctas: <b>{facturarPreview.ya_correctas}</b></p>
              {facturarPreview.a_cambiar === 0 && (
                <p className="text-emerald-600 text-xs">No hay nada para aplicar.</p>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFacturarOpen(false)} data-testid="reaplicar-facturada-cancel">
              Cancelar
            </Button>
            <Button
              onClick={confirmarReaplicarFacturada}
              disabled={facturarAplicando || facturarLoading || !facturarPreview?.a_cambiar}
              data-testid="reaplicar-facturada-confirm"
            >
              {facturarAplicando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
